import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

export default function initAnimations() {
  // initHomeScrollableBlobAnimation();
  animateKnockoutText();
  initFadeUpOnScroll();
}

function initHomeScrollableBlobAnimation() {
  if (!document.body.classList.contains('home')) {
    return;
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const blob = document.getElementById('home-scrollable-blob');
  if (!blob) {
    return;
  }

  const twoColCardsSection = blob.closest('.two-col-cards');

  const rightContentBlock = document.querySelector('.content-w-card-grid--right');
  const leftContentBlock = document.querySelector('.content-w-card-grid--left');
  const masonrySection = document.querySelector('.masonry');

  if (!twoColCardsSection || !rightContentBlock || !leftContentBlock || !masonrySection) {
    return;
  }

  const resetBlobToDefaultStyles = () => {
    gsap.killTweensOf(blob);
    gsap.set(blob, { clearProps: 'all' });
  };

  const setPinnedBlobBase = () => {
    gsap.set(blob, {
      position: 'fixed',
      width: '40vw',
      height: '40vw',
      left: 0,
      top: '50%',
      x: '80vw',
      yPercent: -50,
      scale: 1,
      maxWidth: 'none',
      marginInline: 0,
      zIndex: -1,
      transformOrigin: '50% 50%',
      willChange: 'transform, border-radius, background-color',
    });
  };

  // Two-column cards mostly in view (80% viewport): restore original CSS blob state.
  ScrollTrigger.create({
    trigger: twoColCardsSection,
    start: 'top 80%',
    end: 'bottom bottom',
    onEnter: resetBlobToDefaultStyles,
    onEnterBack: resetBlobToDefaultStyles,
    // markers: true,
  });

  // Right content-with-card block enters: pin blob and convert to 40vw circle at right side.
  ScrollTrigger.create({
    trigger: rightContentBlock,
    start: 'top center',
    // markers: { startColor: 'black', endColor: 'black' },
    onEnter: () => {
      setPinnedBlobBase();
      gsap.to(blob, {
        x: '80vw',
        scale: 1,
        borderRadius: '50%',
        duration: 0.8,
        ease: 'power2.out',
        backgroundColor: 'var(--color-yellowish)',
      });
    },
    onEnterBack: () => {
      setPinnedBlobBase();
      gsap.to(blob, {
        x: '80vw',
        scale: 1,
        borderRadius: '50%',
        duration: 0.8,
        ease: 'power2.out',
        backgroundColor: 'var(--color-yellowish)',
      });
    },
    onLeaveBack: resetBlobToDefaultStyles,
  });

  // Left content-with-card block enters: keep shape and slide blob to the left.
  ScrollTrigger.create({
    trigger: leftContentBlock,
    start: 'top 30%',
    // markers: true,
    onEnter: () => {
      gsap.to(blob, {
        x: '-20vw',
        scale: 1,
        borderRadius: '50%',
        duration: 0.8,
        backgroundColor: 'var(--color-greenish)',
        ease: 'power2.out',
      });
    },
    onEnterBack: () => {
      gsap.to(blob, {
        x: '-20vw',
        scale: 1,
        borderRadius: '50%',
        duration: 0.8,
        ease: 'power2.out',
        backgroundColor: 'var(--color-greenish)',
      });
    },
    onLeaveBack: () => {
      gsap.to(blob, {
        x: '80vw',
        scale: 1,
        borderRadius: '50%',
        duration: 0.8,
        ease: 'power2.out',
        backgroundColor: 'var(--color-yellowish)',
      });
    },
  });

  // Masonry section enters: move blob right again and scale to 60vw circle.
  ScrollTrigger.create({
    trigger: masonrySection,
    start: 'top 70%',
    onEnter: () => {
      gsap.to(blob, {
        x: '60vw',
        y: '20vh',
        scale: 1.5,
        borderRadius: '50%',
        duration: 1,
        ease: 'power2.out',
        backgroundColor: 'var(--color-redish)',
      });
    },
    onEnterBack: () => {
      gsap.to(blob, {
        x: '60vw',
        y: '20vh',
        scale: 1.5,
        borderRadius: '50%',
        duration: 1,
        ease: 'power2.out',
        backgroundColor: 'var(--color-redish)',
      });
    },
    onLeaveBack: () => {
      gsap.to(blob, {
        x: '0vw',
        y: '0vh',
        scale: 1,
        borderRadius: '50%',
        duration: 0.8,
        ease: 'power2.out',
        backgroundColor: 'var(--color-greenish)',
      });
    },
  });
}

function animateKnockoutText() {
  const knockoutTexts = document.querySelectorAll('.knockout');

  if (knockoutTexts) {
    knockoutTexts.forEach((knockoutText) => {
      const text = knockoutText.textContent;
      knockoutText.innerHTML = text
        .split('')
        .map((char) => `<span>${char}</span>`)
        .join('');

      // Create a timeline to sequence the animations
      const knockoutTextTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: knockoutText.parentNode,
          start: 'top 80%',
          end: 'top 100%',
          scrub: false,
          invalidateOnRefresh: true,
          // markers: true,
        },
      });

      // First animate the h2 spans
      knockoutTextTimeline.fromTo(
        knockoutText.querySelectorAll('span'),
        {
          y: '100',
          autoAlpha: 0,
        },
        {
          y: '0',
          autoAlpha: 1,
          duration: 0.3,
          ease: 'power2.out',
          stagger: 0.01,
        },
      );
    });
  }
}

// utility fadeup animation for other elements that we want to animate on scroll with the class name uy-fade-up
function initFadeUpOnScroll() {
  const fadeUpElements = document.querySelectorAll('.uy-fade-up');

  if (fadeUpElements) {
    fadeUpElements.forEach((el) => {
      gsap.fromTo(
        el,
        {
          y: 50,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 70%',
            // end: 'top 70%',
            scrub: false,
            invalidateOnRefresh: true,
            // markers: true,
          },
        },
      );
    });
  }
}
